import React, { useEffect } from 'react';

const CorporatesView = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const programs = [
        {
            title: "Employee Wellness Programs",
            icon: "fa-heartbeat",
            description: "Comprehensive health checkups, vaccination drives, and wellness webinars tailored for your workforce to ensure peak health and productivity."
        },
        {
            title: "Corporate Health Cover",
            icon: "fa-shield-alt",
            description: "Exclusive discounts on medicines, lab tests, and doctor consultations for your employees and their families."
        },
        {
            title: "Bulk Pharmacy Procurement",
            icon: "fa-boxes",
            description: "Seamless procurement of first-aid supplies, essential medicines, and hygiene products for your office premises at wholesale rates."
        }
    ];

    const benefits = [
        { title: "Dedicated Account Manager", icon: "fa-user-tie" },
        { title: "Priority Support & Delivery", icon: "fa-shipping-fast" },
        { title: "Monthly Health Analytics", icon: "fa-chart-pie" },
        { title: "Customizable Health Packages", icon: "fa-cogs" }
    ];

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6">
            <div className="max-w-6xl mx-auto space-y-16">
                
                {/* Hero Section */}
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 flex flex-col md:flex-row items-center p-8 md:p-16">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-sky-50 rounded-full blur-3xl opacity-50 transform translate-x-1/2 -translate-y-1/2"></div>
                    <div className="relative z-10 md:w-1/2 space-y-6 text-center md:text-left">
                        <span className="text-sm font-bold tracking-widest text-sky-600 uppercase">QwickMed For Business</span>
                        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
                            Invest in Your Team's Health & Wellbeing
                        </h1>
                        <p className="text-lg text-slate-600">
                            Empower your workforce with our end-to-end corporate healthcare solutions. From wellness programs to exclusive discounts, we've got your team covered.
                        </p>
                        <button className="bg-sky-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-sky-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-1">
                            Partner With Us
                        </button>
                    </div>
                    <div className="relative z-10 md:w-1/2 mt-12 md:mt-0 flex justify-center">
                        {/* Abstract Corporate Health Graphic */}
                        <div className="w-64 h-64 md:w-80 md:h-80 relative">
                            <div className="absolute inset-0 bg-sky-100 rounded-full mix-blend-multiply opacity-70 animate-pulse"></div>
                            <div className="absolute inset-4 bg-emerald-100 rounded-full mix-blend-multiply opacity-70 animate-pulse" style={{ animationDelay: '1s' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <i className="fas fa-building text-7xl text-sky-600 absolute -ml-12 -mt-12 z-20"></i>
                                <i className="fas fa-notes-medical text-5xl text-emerald-500 absolute ml-16 mt-16 z-30"></i>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Programs Section */}
                <div className="space-y-10">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Corporate Offerings</h2>
                        <p className="text-slate-600">Tailored solutions to fit the healthcare needs of businesses of all sizes.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {programs.map((prog, idx) => (
                            <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md hover:border-sky-200 transition-all group">
                                <div className="w-14 h-14 bg-sky-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-sky-600 transition-colors">
                                    <i className={`fas ${prog.icon} text-2xl text-sky-600 group-hover:text-white transition-colors`}></i>
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 mb-3">{prog.title}</h3>
                                <p className="text-slate-600 leading-relaxed">{prog.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Benefits Section */}
                <div className="bg-slate-900 rounded-3xl p-8 md:p-16 text-white relative overflow-hidden">
                    <div className="absolute right-0 bottom-0 opacity-10">
                        <i className="fas fa-briefcase-medical text-[250px] -mr-10 -mb-10"></i>
                    </div>
                    <div className="relative z-10 space-y-10">
                        <div className="text-center max-w-2xl mx-auto">
                            <h2 className="text-3xl font-bold mb-4">Why Choose QwickMed?</h2>
                            <p className="text-slate-300">We provide a seamless and secure healthcare ecosystem designed specifically for modern corporate enterprises.</p>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {benefits.map((benefit, idx) => (
                                <div key={idx} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 p-6 rounded-2xl text-center hover:bg-slate-700 transition-colors">
                                    <i className={`fas ${benefit.icon} text-3xl text-emerald-400 mb-4`}></i>
                                    <h4 className="font-semibold">{benefit.title}</h4>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="text-center bg-white p-10 rounded-3xl shadow-sm border border-slate-100 max-w-4xl mx-auto">
                    <h3 className="text-2xl font-bold text-slate-800 mb-4">Ready to elevate your team's health?</h3>
                    <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                        Get in touch with our corporate partnerships team to discuss a customized healthcare plan that fits your organization.
                    </p>
                    <a href="mailto:corporate@qwickmed.com" className="inline-flex items-center justify-center bg-sky-50 text-sky-700 font-bold px-8 py-4 rounded-xl hover:bg-sky-100 hover:text-sky-800 transition-colors">
                        <i className="fas fa-envelope mr-3 text-xl"></i> Contact Corporate Sales
                    </a>
                </div>

            </div>
        </div>
    );
};

export default CorporatesView;
